import Store from 'app/store'
import CONSTANTS from 'app/constants'
import PubSub from 'core/pubsub'
import cookies from 'js-cookie'
import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only'
import 'whatwg-fetch'
import _ from 'underscore'
import axios from 'axios'
import Router from 'offline/router'

const CancelToken = axios.CancelToken

function offlineParams(params, method) {
	var base = params || {}
	return Object.assign({}, base, {
		__offlineMethod: method
	})
}

function syncRoutingMode() {
	if (CONSTANTS.OFFLINE.FORCE || !Router.connected) {
		Router.setMode('offline')
		return
	}

	if (CONSTANTS.OFFLINE.LOCAL) {
		Router.setMode('local')
		return
	}

	Router.setMode('online')
}

function shouldHandleLocally(action) {
	syncRoutingMode()
	return Router.shouldHandleLocally(action)
}

function offlineToastMessage(error) {
	if (!error || !error.offline) return null

	if (error.nodata) {
		return 'Offline data is not available yet. Go online and sync first.'
	}

	if (error.unimplemented || error.error === 'This action is not available offline.') {
		return 'This action requires an online connection.'
	}

	return error.error || CONSTANTS.ERROR.UNKNOWN
}

function handleLocalError(error) {
	var message = offlineToastMessage(error)
	if (message) PubSub.$emit('error', message)
	return Promise.reject(error)
}

export default {

	busy: false,
	signal: false,
	controller: false,
	source: {},

	request: function(action, params, isSync) {

		if (_.isArray(action)) action = action.join('/')

		// Check if this should be handled by the offline engine
		if (shouldHandleLocally(action)) {
			return Router.handle(action, offlineParams(params, 'POST')).then(function(json) {
				// Mimic the normal response flow: verify session, set permissions
				if (json.session) Store.dispatch('session/verify', json.session)
				if (json.permissions) Store.commit('permissions/set', json.permissions)
				return json
			}, handleLocalError)
		}

		if (this.busy && !params.silent) {

			this.controller.abort()

		}

		isSync = isSync || false

		this.controller = new AbortController()
		this.signal = this.controller.signal

		this.busy = true

		var self = this

		return new Promise(function(resolve, reject) {

			var headers = {
				'content-type': 'application/json'
			}

			if (cookies.get('token') !== undefined) headers['Authorization'] = cookies.get('token')

			window.fetch(CONSTANTS.API + action + '.json', {
				synchronous: isSync,
				method: 'POST',
				headers: headers,
				credentials: 'include',
				signal: self.signal,
				body: JSON.stringify(params)
			}).then(function(response) {

				self.busy = false

				if (!response.ok) {

					response.json().then(function(json) {

						PubSub.$emit('error', json.error)

						reject(json)

					}, function(error) {

						PubSub.$emit('error', error)

						reject({
							error: error
						})

					})

				} else {

					return response.json().then(function(json) {

						Store.dispatch('session/verify', json.session).then(function() {

							if (json.permissions) Store.commit('permissions/set', json.permissions)

							resolve(json)
									
						})

					}, function(error) {

						PubSub.$emit('error', error)

						reject({
							error: error
						})

					})

				}

			}.bind(this), function(error) {

				PubSub.$emit('error', CONSTANTS.ERROR.UNKNOWN)

				reject({
					error: error
				})

			})

		}, function() {

			PubSub.$emit('error', CONSTANTS.ERROR.UNKNOWN)

		})

	},

	get: function(url, params, page) {

		params = params || {}
		page = page || false

		if (page) params['page'] = page

		if (_.isArray(url)) url = url.join('/')

		if (shouldHandleLocally(url)) {
			return Router.handle(url, offlineParams(params, 'GET')).then(function(json) {
				if (json.session) Store.dispatch('session/verify', json.session)
				if (json.permissions) Store.commit('permissions/set', json.permissions)
				return json.data || json
			}, handleLocalError)
		}

		var storageUrl = url + JSON.stringify(params)

		if(this.source[storageUrl] && url !== 'session' && url !== 'groups/verify' && url !== 'users/verify') this.source[storageUrl].cancel()

		this.source[storageUrl] = CancelToken.source()

		return new Promise(function(resolve, reject) {

			console.log('api', 'get', url, params)

			axios.get(CONSTANTS.API + url + '.json', {
				cancelToken: this.source[storageUrl].token,
				headers: {
					'content-type': 'application/json',
					'Cache-Control': 'no-cache',
					'Pragma': 'no-cache',
					'Expires': '0',
				},
				params: params
			}).then(function(response) {

				if (_.isObject(response)) {

					this.response(resolve, reject, response)

				} else {

					reject(null)

				}

			}.bind(this), function(json) {

				reject((json.response) ? json.response : null)

			})

		}.bind(this))

	},

	post: function(url, params, options = {}) {

		params = params || {}

		if (_.isArray(url)) url = url.join('/')

		if (shouldHandleLocally(url)) {
			return Router.handle(url, offlineParams(params, 'POST')).then(function(json) {
				if (json.session) Store.dispatch('session/verify', json.session)
				if (json.permissions) Store.commit('permissions/set', json.permissions)
				return json.data || json
			}, handleLocalError)
		}

		if(this.source[url] && !options.nonblock) this.source[url].cancel()

		this.source[url] = CancelToken.source()

		return new Promise(function(resolve, reject) {

			console.log('api', 'post', url, params)

			axios.post(CONSTANTS.API + url + '.json', params, {
				cancelToken: this.source[url].token,
				headers: {
					'content-type': 'application/json'
				}
			}).then(function(response) {

				if (_.isObject(response)) {

					this.response(resolve, reject, response)

				} else {

					reject({})

				}

			}.bind(this), function(json) {

				reject((json.response) ? json.response.data.data : null)

			})

		}.bind(this))

	},

	patch: function(url, params) {

		params = params || {}

		if (_.isArray(url)) url = url.join('/')

		if (shouldHandleLocally(url)) {
			return Router.handle(url, offlineParams(params, 'PATCH')).then(function(json) {
				if (json.session) Store.dispatch('session/verify', json.session)
				if (json.permissions) Store.commit('permissions/set', json.permissions)
				return json.data || json
			}, handleLocalError)
		}

		if(this.source[url]) this.source[url].cancel()

		this.source[url] = CancelToken.source()

		return new Promise(function(resolve, reject) {

			console.log('api', 'patch', url, params)

			axios.patch(CONSTANTS.API + url + '.json', params, {
				cancelToken: this.source[url].token,
				headers: {
					'content-type': 'application/json'
				}
			}).then(function(response) {

				if (_.isObject(response)) {

					this.response(resolve, reject, response)

				} else {

					reject(null)

				}

			}.bind(this), function(json) {

				reject((json.response) ? json.response.data.data : null)

			})

		}.bind(this))

	},

	put: function(url, params) {

		params = params || {}

		if (_.isArray(url)) url = url.join('/')

		if (shouldHandleLocally(url)) {
			return Router.handle(url, offlineParams(params, 'PUT')).then(function(json) {
				if (json.session) Store.dispatch('session/verify', json.session)
				if (json.permissions) Store.commit('permissions/set', json.permissions)
				return json.data || json
			}, handleLocalError)
		}

		if(this.source[url]) this.source[url].cancel()

		this.source[url] = CancelToken.source()

		return new Promise(function(resolve, reject) {

			console.log('api', 'put', url, params)

			axios.put(CONSTANTS.API + url + '.json', params, {
				cancelToken: this.source[url].token,
				headers: {
					'content-type': 'application/json'
				}
			}).then(function(response) {

				if (_.isObject(response)) {

					this.response(resolve, reject, response)

				} else {

					reject(null)

				}

			}.bind(this), function(json) {

				reject((json.response) ? json.response.data.data : null)

			})

		}.bind(this))

	},

	delete: function(url, params) {

		params = params || {}

		if (_.isArray(url)) url = url.join('/')

		if (shouldHandleLocally(url)) {
			return Router.handle(url, offlineParams(params, 'DELETE')).then(function(json) {
				if (json.session) Store.dispatch('session/verify', json.session)
				if (json.permissions) Store.commit('permissions/set', json.permissions)
				return json.data || json
			}, handleLocalError)
		}

		if(this.source[url]) this.source[url].cancel()

		this.source[url] = CancelToken.source()

		return new Promise(function(resolve, reject) {

			console.log('api', 'delete', url)

			axios.delete(CONSTANTS.API + url + '.json', {
				cancelToken: this.source[url].token,
				data: params,
				headers: {
					'content-type': 'application/json'
				}
			}).then(function(response) {

				if (_.isObject(response)) {

					this.response(resolve, reject, response)

				} else {

					reject(null)

				}

			}.bind(this), function(json) {

				reject((json.response) ? json.response.data.data : null)

			})

		}.bind(this))

	},

	response: function(resolve, reject, response) {

		this.busy = false

		Store.dispatch('session/verify', response.data.session).then(function() {

			if (response.data.permissions) Store.commit('permissions/set', response.data.permissions)

			resolve(response.data.data || response.data)
							
		})

	}

}
